import { compact } from 'lodash';
import { useQuery } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { withGlobalError } from '@/react-tools/react-query';
import { useRegistryRepositories } from '@/react/portainer/registries/queries/useRegistryRepositories';
import { Registry } from '@/react/portainer/registries/types/registry';

import { Chart, HelmChartsResponse } from '../types';

import { queryKeys } from './query-keys';

type HelmRegistry = {
  id?: Registry['Id'];
  url?: string;
};

/**
 * React hook to fetch helm charts from the provided registry.
 * Charts are loaded based on the registry type (OCI or HTTP).
 *
 * @param userId User ID
 * @param environmentId Environment ID
 * @param registry Registry information containing id and url
 * @param isOCI Flag indicating if the registry is OCI
 * @returns Query result containing helm charts
 */
export function useHelmChartList(
  userId: number,
  environmentId: number,
  registry: HelmRegistry,
  isOCI: boolean,
  showError = true
) {
  const httpQuery = useHelmHTTPChartList(
    userId,
    registry.url ?? '',
    !isOCI,
    showError
  );
  const ociQuery = useHelmOCIChartList(
    registry.id ?? 0,
    registry.url ?? '',
    environmentId,
    isOCI,
    showError
  );

  if (isOCI) {
    return ociQuery;
  }

  return httpQuery;
}

/**
 * React hook to fetch helm charts from the provided HTTP repository.
 * Charts are loaded from the specified repository URL.
 *
 * @param userId User ID
 * @param repository Repository URL to fetch charts from
 * @param enabled Flag indicating if the query should be enabled
 * @returns Query result containing helm charts
 */
function useHelmHTTPChartList(
  userId: number,
  repository: string,
  enabled: boolean,
  showError = true
) {
  return useQuery({
    queryKey: queryKeys.charts(userId, repository),
    queryFn: () => getChartsFromRepo(repository),
    enabled: !!userId && !!repository && enabled,
    // one request takes a long time, so fail early to get feedback to the user faster
    retry: false,
    ...(showError &&
      withGlobalError(`Unable to retrieve Helm charts from ${repository}`)),
    cacheTime: 1000 * 60 * 60 * 8, // 8 hours so that the chart list populates faster (keep stale time the same to always revalidate)
  });
}

/**
 * React hook to fetch helm charts from the provided OCI registry.
 * Charts are loaded based on the registry ID and URL.
 *
 * @param registryId Registry ID
 * @param registryUrl Registry URL
 * @param environmentId Environment ID
 * @param enabled Flag indicating if the query should be enabled
 * @returns Query result containing helm charts
 */
function useHelmOCIChartList(
  registryId: Registry['Id'],
  registryUrl: string,
  environmentId: number,
  enabled: boolean,
  showError = true
) {
  return useRegistryRepositories(
    registryId,
    {
      endpointId: environmentId,
      // hasMediaType checks if a manifest media type matches the expected media type.
      // https://helm.sh/blog/helm-oci-mediatypes/
      mediaType: 'application/vnd.cncf.helm.config.v1+json',
    },
    {
      select: (repositories): Chart[] =>
        repositories.map((repo) => ({
          repo: registryUrl,
          name: repo,
          description: '',
          version: '',
          versions: [],
        })),
      enabled,
      cacheTime: 1000 * 60 * 60 * 8, // 8 hours so that the chart list populates faster (keep stale time the same to always revalidate)
    },
    showError
  );
}

async function getChartsFromRepo(repo: string): Promise<Chart[]> {
  try {
    // Construct the URL with required repo parameter
    const response = await axios.get<HelmChartsResponse>('templates/helm', {
      params: { repo },
    });

    return compact(
      Object.values(response.data.entries).map((versions) =>
        versions[0]
          ? {
              ...versions[0],
              repo,
              // versions are within this response too, so we don't need a new query to fetch versions when this is used
              versions: versions.map((v) => v.version),
            }
          : null
      )
    );
  } catch (error) {
    // Ignore errors from chart repositories as some may error but others may not
    return [];
  }
}
